import type { Patient, Reference } from '@medplum/fhirtypes'
import type { ExtendedObservation } from '#src/models/observation.ts'
import { groupObservationCaptures } from '#src/models/observationCaptures.ts'
import {
  type ObservationValue,
  observationValueOf,
} from '#src/observation-logic/value.ts'
import { observationCodeFamily } from '#src/terminology/library.ts'

// Enough recent records to hold the newest capture whole: a multi-select
// answer is one record per chosen value.
const LATEST_CAPTURE_WINDOW = 20

// The participant's most recent records for a code (and the codes it is
// mapped to), newest first, for `latestCaptureMembers`. A node frozen to the
// latest value reads this, in the editor and on the server alike.
export function latestCaptureQuery({
  subject,
  system,
  code,
}: {
  subject: Reference<Patient> | string
  system: string
  code: string
}) {
  return {
    patient: typeof subject === 'string' ? subject : (subject.reference ?? ''),
    code: observationCodeFamily({ system, code }),
    'status:not': 'entered-in-error',
    _sort: '-date',
    _count: LATEST_CAPTURE_WINDOW,
  }
}

// The newest capture among the records: its one record, or every record of
// a multi-select answer.
export function latestCaptureMembers(
  records: readonly ExtendedObservation[]
): ExtendedObservation[] {
  return groupObservationCaptures(records)[0]?.members ?? []
}

// A capture's value: its one record's, or, for a multi-select answer, every
// chosen answer as one coded value (a condition matches any of them).
export function captureValue(
  members: readonly ExtendedObservation[]
): ObservationValue | null {
  const values = members.flatMap((member) => {
    const value = observationValueOf(member)
    return value ? [value] : []
  })
  if (values.length > 1 && values.every((value) => value.kind === 'coding')) {
    return {
      kind: 'coding',
      value: values.flatMap((value) =>
        value.kind === 'coding' ? value.value : []
      ),
    }
  }
  return values[0] ?? null
}
